import { useMemo, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { useUsername } from '../hooks/useUsername'
import { useAuthStore } from '../store/auth'
import { usePageHeader } from '../hooks/usePageHeader'
import { useDefaultsStore } from '../store/defaults'
import { useAuthorTheme } from '../hooks/useAuthorTheme'

const PRIVACY_LABELS = ['private', 'friends', 'friends-of-friends', 'public']

interface TagsetRecord {
  id: string
  name: string
  privacy: number
  tags: string[]
}

function TagsetRow({
  tagset: initial,
  username,
  onDeleted,
}: {
  tagset: TagsetRecord
  username: string
  onDeleted: (name: string) => void
}) {
  const [tagset, setTagset] = useState(initial)
  const [tagInput, setTagInput] = useState('')
  const [deleting, setDeleting] = useState(false)

  async function handleAddTag(e: React.FormEvent) {
    e.preventDefault()
    const tag = tagInput.trim().toLowerCase()
    if (!tag || tagset.tags.includes(tag)) return
    try {
      await api.post(`/users/${username}/tagsets/${encodeURIComponent(tagset.name)}/tags/${encodeURIComponent(tag)}`)
      setTagset((prev) => ({ ...prev, tags: [...prev.tags, tag].sort() }))
      setTagInput('')
    } catch { /* ignore */ }
  }

  async function handleRemoveTag(tag: string) {
    try {
      await api.delete(`/users/${username}/tagsets/${encodeURIComponent(tagset.name)}/tags/${encodeURIComponent(tag)}`)
      setTagset((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }))
    } catch { /* ignore */ }
  }

  async function handleDelete() {
    if (!confirm(`Delete tagset "${tagset.name}"?`)) return
    setDeleting(true)
    try {
      await api.delete(`/users/${username}/tagsets/${encodeURIComponent(tagset.name)}`)
      onDeleted(tagset.name)
    } catch { setDeleting(false) }
  }

  return (
    <div style={{ borderBottom: '1px solid var(--color-fg)', padding: '1rem 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
          <Link to={`/~${username}/tagsets/${encodeURIComponent(tagset.name)}`} style={{ fontWeight: 'bold' }}>
            {tagset.name}
          </Link>
          <span style={{ opacity: 0.5, fontSize: '0.8rem' }}>[{PRIVACY_LABELS[tagset.privacy] ?? tagset.privacy}]</span>
        </div>
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          style={{ width: 'auto', fontSize: '0.8rem', opacity: 0.5 }}
        >
          delete
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
        {tagset.tags.length === 0 && (
          <span style={{ opacity: 0.4, fontSize: '0.85rem' }}>no tags yet</span>
        )}
        {tagset.tags.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.15rem',
              background: 'var(--color-fg)', color: 'var(--color-bg)',
              padding: '0.1rem 0.4rem', borderRadius: 2, fontSize: '0.85rem',
            }}
          >
            #{tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.1rem', lineHeight: 1, fontSize: '1rem' }}
              aria-label={`remove ${tag}`}
            >×</button>
          </span>
        ))}
        <form onSubmit={handleAddTag} style={{ display: 'inline-flex', gap: '0.3rem' }}>
          <input
            placeholder="add tag…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            style={{ width: 100, fontSize: '0.8rem' }}
          />
          <button type="submit" style={{ width: 'auto', fontSize: '0.8rem' }}>+</button>
        </form>
      </div>
    </div>
  )
}

export function Tagsets() {
  const username = useUsername()
  const currentUser = useAuthStore((s) => s.username)
  const isOwner = currentUser === username
  const { defaults } = useDefaultsStore()
  useAuthorTheme(username ?? undefined)

  const [tagsets, setTagsets] = useState<TagsetRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [newName, setNewName] = useState('')
  const [newPrivacy, setNewPrivacy] = useState(() => defaults.tagset)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    if (!username || !isOwner) { setLoading(false); return }
    setLoading(true)
    api.get<TagsetRecord[]>(`/users/${username}/tagsets`)
      .then(setTagsets)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [username, isOwner])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    setCreating(true)
    setCreateError('')
    try {
      const created = await api.post<TagsetRecord>(`/users/${username}/tagsets`, { name, privacy: newPrivacy })
      setTagsets((prev) => [...prev, created])
      setNewName('')
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setCreating(false)
    }
  }

  const titleNode = useMemo(() => (
    username ? (
      <><Link to={`/~${username}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{username}</Link>{' / tagsets'}</>
    ) : 'tagsets'
  ), [username])

  usePageHeader(titleNode)

  return (
    <div>
      <p style={{ opacity: 0.5, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
        a tagset is a named combination of tags — it shows all entries that carry every tag in the set
      </p>

      {!isOwner && (
        <p style={{ opacity: 0.5 }}>only the account owner can manage tagsets</p>
      )}

      {isOwner && (
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
          <input
            placeholder="new tagset name…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ width: 200 }}
          />
          <select value={newPrivacy} onChange={(e) => setNewPrivacy(Number(e.target.value))} style={{ width: 'auto' }}>
            <option value={0}>private</option>
            <option value={1}>friends</option>
            <option value={2}>friends of friends</option>
            <option value={3}>public</option>
          </select>
          <button type="submit" disabled={creating} style={{ width: 'auto' }}>
            {creating ? 'creating…' : 'create tagset'}
          </button>
          {createError && <span style={{ color: 'var(--color-1)', fontSize: '0.85rem' }}>{createError}</span>}
        </form>
      )}

      {loading ? (
        <p>loading…</p>
      ) : error ? (
        <p style={{ color: 'var(--color-1)' }}>{error}</p>
      ) : tagsets.length === 0 ? (
        <p style={{ opacity: 0.5 }}>no tagsets yet</p>
      ) : (
        tagsets.map((ts) => (
          <TagsetRow
            key={ts.id}
            tagset={ts}
            username={username ?? ''}
            onDeleted={(name) => setTagsets((prev) => prev.filter((t) => t.name !== name))}
          />
        ))
      )}
    </div>
  )
}
